<template>
  <div id="bpmn-canvas"></div>
</template>

<script>
  import BpmnJS from 'bpmn-js/dist/bpmn-navigated-viewer.production.min'
  import {objectBy} from 'src/utilities/utils'
  import {mapGetters} from 'vuex'
  import Constants from 'src/utilities/constants'
  import moment from 'moment'

  export default {
    props: ['data'],
    data () {
      return {
        viewer: null,
        canvas: null,
        overlays: null,
        elementRegistry: null,
        overlayIds: [],
        loadingOverlayId: null
      }
    },
    computed: {
      ...mapGetters({
        bpmnXML: 'camunda/processDefinitionXml',
        activityInstances: 'camunda/activityInstances',
        activeBpmnTasks: 'camunda/tasks'
      })
    },
    methods: {
      reloadOverlaysWithDelay () {
        this.clearOverlays()
        this.showLoadingOverlay()
        setTimeout(async () => {
          await this.refreshJob () // needed by the final task completion
          await this.fetchActivityInstances()
          await this.fetchTasks()
          this.maybeMarkCompleted()
          this.renderTaskAndGatewayOverlays()
          this.hideLoadingOverlay()
        }, 4000) // 4 seconds seems to be enough time to wait for Camunda to start new tasks or complete the process instance
      },
      async refreshJob () {
        await this.$store.dispatch('jobs/fetchByIdAndReplace', this.data.id)
      },
      async fetchProcessDefinitionXml () {
        await this.$store.dispatch('camunda/fetchProcessDefinitionXml', this.data.processDefinitionId)
      },
      async fetchActivityInstances () {
        await this.$store.dispatch('camunda/fetchActivityInstances', this.data.processInstanceId)
      },
      async fetchTasks () {
        await this.$store.dispatch('camunda/fetchTasks', this.data.processInstanceId)
      },
      loadViewer () {
        this.viewer = new BpmnJS({ container: '#bpmn-canvas' })
        this.canvas = this.viewer.get('canvas');
        this.overlays = this.viewer.get('overlays')
        this.viewer.get('canvas').zoom('fit-viewport')
        this.elementRegistry = this.viewer.get('elementRegistry')
      },
      getBaseOverlayConfig () {
        return {
          // configure show={ minZoom: 0.6 } to hide overlays at low zoom levels
          show: {
            minZoom: 0.7
          }
        }
      },
      showLoadingOverlay () {
        let overlayConfig = this.getBaseOverlayConfig()

        this.elementRegistry.forEach(element => {
          const bo = element.businessObject
          if (bo.$instanceOf('bpmn:Process')) {
            overlayConfig.position = {
              top: 50,
              left: 250
            }
            overlayConfig.html = '<div class="bpmn-loading" style="width: 600px;margin:0 auto;"><h2>Reloading ' + bo.name + '</h2><img src="static/img/loading.gif" style="width: 300px;" /></div>'
            this.loadingOverlayId = this.overlays.add(bo.id, overlayConfig)
          }
        })
      },
      hideLoadingOverlay () {
        this.overlays.remove(this.loadingOverlayId)
      },
      maybeMarkCompleted () {
        let overlayConfig = this.getBaseOverlayConfig()

        this.elementRegistry.forEach(element => {
          const bo = element.businessObject
          if (bo.$instanceOf('bpmn:Process')) {
            if (this.data.actualCompletionDate) {
              overlayConfig.position = {
                top: 50,
                left: 250
              }
              overlayConfig.html = '<div class="bpmn-completed"><h2>Completed: ' + moment(this.data.actualCompletionDate).format('MMMM Do YYYY, h:mm a') + '</h2></div>'
              this.overlays.add(bo.id, overlayConfig)
            }
          }
        })
      },
      renderTaskAndGatewayOverlays () {
        if (this.activityInstances && this.activityInstances.childActivityInstances) {
          this.activityInstances.childActivityInstances.forEach((activityInstance) => {
            // LIMITATION - Doesn't recurse through nested sub-processes
            if (activityInstance.activityType === Constants.CAMUNDA.ACTIVITY_TYPES.SUB_PROCESS) {
              activityInstance.childActivityInstances.forEach((childActivityInstance) => {
                const activeBpmnTask = objectBy(this.activeBpmnTasks, 'taskDefinitionKey', childActivityInstance.activityId)
                this.doRenderTaskAndGatewayOverlays(childActivityInstance, activeBpmnTask)
              })
            } else {
              const activeBpmnTask = objectBy(this.activeBpmnTasks, 'taskDefinitionKey', activityInstance.activityId)
              this.doRenderTaskAndGatewayOverlays(activityInstance, activeBpmnTask)
            }
          })
        }
      },
      doRenderTaskAndGatewayOverlays (activityInstance, activeBpmnTask) {
        let overlayConfig = this.getBaseOverlayConfig()

        if (activityInstance.activityType === Constants.CAMUNDA.ACTIVITY_TYPES.USER_TASK) {
          overlayConfig.position = {
            bottom: 82,
            right: 104
          }

          if (activeBpmnTask) {
            overlayConfig.html = '<a href="#/projects/' + this.data.projectId + '?taskId=' + activeBpmnTask.description + '"><div class="bpmn-active-element bpmn-active-task"></div></a>'
          } else {
            return
          }
        } else if (activityInstance.activityType === Constants.CAMUNDA.ACTIVITY_TYPES.INCLUSIVE_GATEWAY
          || activityInstance.activityType === Constants.CAMUNDA.ACTIVITY_TYPES.PARALLEL_GATEWAY) {
          overlayConfig.position = {
            bottom: 41,
            right: 41
          }
          overlayConfig.html = '<div class="bpmn-active-element bpmn-active-gateway"></div>'
        } else if (activityInstance.activityType === Constants.CAMUNDA.ACTIVITY_TYPES.INTERMEDIATE_TIMER) {
          overlayConfig.position = {
            bottom: 37,
            right: 37
          }
          overlayConfig.html = '<div class="bpmn-active-element bpmn-active-timer"></div>'
        }
        this.overlayIds.push(this.overlays.add(activityInstance.activityId, overlayConfig))
      },
      clearOverlays () {
        this.overlayIds.forEach(id => {
          this.overlays.remove(id)
        })
      },
      async loadEverything () {
        await this.fetchProcessDefinitionXml()
        await this.fetchActivityInstances()
        await this.fetchTasks()
        this.loadViewer()

        this.viewer.importXML(this.bpmnXML, (err) => {
          if (err) {
            console.error('error rendering', err)
            return
          }
          this.renderTaskAndGatewayOverlays()
          this.maybeMarkCompleted()
        })
      }
    },
    mounted () {
      this.$eventHub.$on('taskCompletedOrCancelled', this.reloadOverlaysWithDelay)
      this.loadEverything()
    },
    destroyed () {
      this.$eventHub.$off('taskCompletedOrCancelled')
    }
  }
</script>

<style>
  #bpmn-canvas {
    height: calc(100% - 150px);
    padding: 0;
    margin: 0;
  }
  .bpmn-active-element {
    border: 5px solid lime;
    text-align: center;
  }
  .bpmn-active-task {
    border-radius: 10px;
    padding-top: 50px;
    min-height: 83px;
    min-width: 106px;
  }
  .bpmn-active-gateway {
    border-radius: 25px;
    min-height: 32px;
    min-width: 32px;
  }
  .bpmn-active-timer {
    border-radius: 25px;
    min-height: 38px;
    min-width: 38px;
  }
  .bpmn-completed {
    min-width: 750px;
    color: red;
  }
  .bpmn-loading {
    width: calc(100% - 100px);
    text-align: center;
  }
</style>
